import {
  AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit,
  Output
} from '@angular/core';
import {Conversation, ConversationMessage} from '../../../store/collaboration.models';
import {Guid} from 'guid-typescript';
import {BehaviorSubject, Subject} from 'rxjs';
import {ChatEditorLinkTypes, ChatEditorOutput} from '@shared/models/chat-editor.model';
import {
  ActivityFeedType,
  ActivityFeedInfo
} from '../../../store/activity.model';
import {
  DeleteConversationMessageCommand, EditConversationMessageCommand,
  SendMessageCommand
} from '../../../store/collaboration.command';
import {ContentScope, PlatformCommand, PlatformCommandResponse} from '@shared/commands/command-base';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {Store} from '@ngrx/store';
import {getClaims, getUserInfo} from '@shared/store/selectors/security.selector';
import {CommandService} from '@core/services/command.service';
import {take, takeUntil} from 'rxjs/operators';
import {StringUtility} from '@shared/utility/string-utility';
import {FileManagementService} from '@core/services/file-management.service';
import {DownloadFileCommand} from '../../../../../file-manager/commands/create-directory.command';
import {Router} from '@angular/router';
import {DialogService} from '@shared/services/dialog-service';
import {GraphqlContentScope} from '@shared/models/graphql-content-scope.model';
import {CollaborationState} from '../../../store/collaboration.reducer';
import {
  getcurrentEditorId, getDeletedMessageId, getEditedMessage,
  getNewChat
} from '../../../store/collaboration.selector';
import {GraphQLService} from "@core/services/graphql.service";
import {CollaborationQuery} from "../../../store/collaboration.query";
import {CollaborationConfig} from "../../../store/collaboration.config";
import {SnackBarService} from "@core/services/snack-bar.service";
import {TimeZoneService} from "@core/services/time-zone.service";
import {ActivityDateRangeTypeScope} from "../../../store/activity-date-range-type-scope.model";
import {ActivityFilterDateRangeTypes} from "@shared/models/activity-tracker-header.model";
import {EditConversationMessageSuccessAction, SendMessageSuccessAction, SetCurrentTextEditorId} from '../../../store/collaboration.action';
import  moment_ from 'moment';
import {ActivityFilterService} from "../../../services/activity-filter.service";
import {Contact} from "@features/contacts/store/contact.model";
import {FlexSheetNavigationService} from '@features/spreadsheet/services/flex-sheet-navigation.service';
import {FlexSheetService} from '@features/spreadsheet/services/flex-sheet.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {PlatformQueryService} from '@core/services/platform-query.service';

@Component({
    standalone: false,
    selector: 'particle-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss']
})
export class ActivityComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() conversation: Conversation; // should be a conversation
  @Input() allContacts: any[] = [];
  @Input() hashTagList: any[] = [];
  @Input() isMagicLinkOnly: boolean = false;
  @Input() hideGotoPage: boolean = false;
  @Input() impersonationId = '';
  @Input() feedInfo: ActivityFeedInfo = null;
  mention$: BehaviorSubject<Contact> = new BehaviorSubject<Contact>(null);
  @Output() newFeedInfo$: EventEmitter<ActivityFeedInfo> = new EventEmitter();
  anchors: any[] = [];
  dynamicDivId = Guid.create().toString();
  showEditor: boolean = false;
  textEditorId = Guid.create().toString();
  replyToMessage: ConversationMessage;
  editingMessage: ConversationMessage;
  focusEditorOnChatEditor$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  userInfo$: any;
  userInfo: any;
  // need to improve
  commands: Map<string, PlatformCommand> = new Map<string, PlatformCommand>();
  public unsubscribe$ = new Subject<void>();
  lowestSequenceNumber: number = 1; // 1 is for initial message and always there will be an initial message
  editorType: string = 'Reply';
  messageText: string = '';
  draftCommentText: string = '';
  isMagicLinkUser = false;
  hasShareId = false;
  gotoBtnHoverMessage = 'Go to Page';
  gotoBtnDisableHoverMessage = '<div><strong>This information is not available to view via the current workflow.</strong><div class="m-t-10">To view this information, please open the scenario directly from the solution menu and open the Activity sidebar for the appropriate screen.</div></div>';

  constructor(private securityStore: Store<SecurityState>,
              private fileManagementService: FileManagementService,
              public dialogService: DialogService,
              private timeZoneService: TimeZoneService,
              private activityFilterService: ActivityFilterService,
              private snackBarService: SnackBarService,
              public collaborationStore: Store<CollaborationState>,
              public changeDetectorRef: ChangeDetectorRef,
              private graphQLService: GraphQLService,
              private router: Router,
              private commandService: CommandService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private flexSheetService: FlexSheetService,
              private flexSheetNavigationService: FlexSheetNavigationService,
              private platformQueryService: PlatformQueryService) {}

  ngOnInit() {
    this.userInfo$ = this.securityStore.select(getUserInfo);
    this.userInfo$.subscribe(userInfo => {
      this.userInfo = userInfo;
      this.prepareConversation();
    });
    this.securityStore.select(getClaims).pipe(take(1)).subscribe(claims => {
      if (claims['MagicLinkId'] && claims['MagicLinkId'].length > 0) {
        this.isMagicLinkUser = true;
        this.gotoBtnHoverMessage = this.gotoBtnDisableHoverMessage;
      }
    });
    if (this.platformQueryService?.platformLinkModel?.fieldValues['ShareId']) {
      this.hasShareId = true;
      this.gotoBtnHoverMessage = this.gotoBtnDisableHoverMessage;
    }
    this.collaborationStore.select(getNewChat).pipe(takeUntil(this.unsubscribe$)).subscribe((newReply: any) => {
      if (newReply && this.conversation && this.conversation.id === newReply.conversationId) {
        this.handleRealTimeReplyMessageUpdates(newReply);
      }
    });

    this.collaborationStore.select(getEditedMessage).pipe(takeUntil(this.unsubscribe$)).subscribe((editedMessage: any) => {
      if (editedMessage && this.conversation && this.conversation.id === editedMessage.conversationId) {
        this.handleEditedMessageUpdate(editedMessage);
      }
    });

    this.collaborationStore.select(getDeletedMessageId).pipe(takeUntil(this.unsubscribe$)).subscribe((deletedMessageId: any) => {
      if (deletedMessageId && this.conversation) {
        this.handleDeletedMessageUpdate(deletedMessageId);
      }
    });

    this.collaborationStore.select(getcurrentEditorId).pipe(takeUntil(this.unsubscribe$)).subscribe((currentEditorId: string) => {
      //console.log('currentEditorId:', currentEditorId);
      this.onEditorIdChange(currentEditorId);
    });
  }

  ngAfterViewInit(): void {
  }

  private handleRealTimeReplyMessageUpdates(newReply: ConversationMessage) {
    if (this.conversation.messages && this.conversation.messages.findIndex(c => c.id === newReply.id) === -1) {
      this.conversation.messageCount++;
      this.conversation.showLoadMoreMessage = true;
      this.conversation.messages.push(this.setMessagePreferences(newReply));
    }
  }

  private onEditorIdChange(currentEditorId: string) {
    if(currentEditorId && this.textEditorId !== currentEditorId) {
      this.showEditor = false;
    }
  }

  contextClickedHandler(event: any, messageId: string) {
    event.stopPropagation();
    // todo: Shakil put a limit on loop
    let id = '';
    let title = '';
    let selectedElement: any;
    const paths = event.composedPath();
    // console.log('paths:', paths);
    if (paths && paths.length > 0) {
      const len = paths.length;
      for (let i = 0; i < len; i++) {
        const element = paths[i];
        if (element.nodeType === 1 && element.nodeName && element.nodeName.toLowerCase() === 'a' && element.id) {
          id = element.id;
          title = element.text;
          selectedElement = element;
          //console.log('got element:', selectedElement);
          break;
        }
      }
    }
    if (!id) {
      return;
    }
    //console.log('current link clicked id:', id, messageId);
    const reg = /\[([^\]]+)]\[([^\]]+)]/;
    const itemInfo = id.match(reg);
    if (itemInfo && itemInfo.length === 3) {
      let itemKey = itemInfo[1];
      const itemid = itemInfo[2];
      if (itemKey.split(':').length > 0) {
        itemKey = itemKey.split(':')[0];
      }
      //console.log('itemKey:', itemKey, itemInfo[1]);
      switch (itemKey.toLowerCase()) {
        case ChatEditorLinkTypes.Link.toLowerCase():
          // open by target and href
          break;
        case ChatEditorLinkTypes.CurrentPage.toLowerCase():
          this.router.navigateByUrl(itemid);
          break;
        case ChatEditorLinkTypes.File.toLowerCase():
          // we will always allow client file to download
          const fileId = itemid;
          const fileName = selectedElement.getAttribute('data-fileName');
          const isClientFileData = selectedElement.getAttribute('data-isclientfile');
          let isClientFile: boolean = false;
          if (isClientFileData) {
            isClientFile = isClientFileData.toLowerCase() === 'true';
          }
          this.downloadFile(fileId, isClientFile, fileName);
          break;
        case ChatEditorLinkTypes.User.toLocaleLowerCase():
          break;
        default:
          let feedType = this.activityFilterService.getFeedType(itemKey);
          let feedId = (feedType === ActivityFeedType.ItemsKey) ? id: itemid;
          this.onAddNewFeed(feedType, feedId, title, false);
      }
    }
  }

  onCancelReply(event) {
    if (event) { this.replyToMessage = null; }
  }

  ngOnDestroy(): void {
    this.commands.clear();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    /*    if (this.anchors && this.anchors.length > 0) {
          for (var i = 0; i < this.anchors.length; i++) {
            this.anchors[i].removeEventListener('click', this.contextClickedHandler, false);
          }
        }*/
  }
  /*  onClick($event) {
      console.log('on lcick:', $event);
      contextClickedHandler($eve);
    }*/
  onChangeReplyMenuItem(action, message) {
    switch (action) {
      case 'Edit':
        this.setEditMessageContext(message);
        break;
      case 'Reply':
        this.setReplyContext(message);
        break;
      case 'Archive':
        break;
      case 'Delete':
        this.removeMessage(message);
        break;
      case 'Details':
        break;
      case 'ShowActivityOnDate':
        this.showActivityOnDate(message.createdDate);
        break;
      case 'GoToPage':
        this.goToPage(message);
        break;
      default :
        break;
    }
  }

  private setReplyContext(message: any) {
    this.showEditor = true;
    this.editorType = 'Reply';
    this.replyToMessage = message;
    this.messageText = this.draftCommentText;
    this.collaborationStore.dispatch(new SetCurrentTextEditorId(this.textEditorId));
    this.focusEditor(this.textEditorId);
  }


  private setEditMessageContext(message: any) {
    this.showEditor = true;
    this.editorType = 'EditMessage';
    this.messageText = message.message;
    this.editingMessage = message;
    this.collaborationStore.dispatch(new SetCurrentTextEditorId(this.textEditorId));
    this.focusEditor(this.textEditorId);
  }

  onCancelChatEditor($event?) {
    this.showEditor = false;
    this.replyToMessage = null;
    this.draftCommentText = '';
  }

  onEditorValueChange($event) {
    this.draftCommentText = $event;
  }

  sendMessage(chatEditorOutput: ChatEditorOutput) {
    this.draftCommentText = '';
    const commentText = chatEditorOutput.chatText;
    const attachment = chatEditorOutput.attachment;
    //console.log('reply:', commentText, attachment);
    if (commentText.trim().length === 0 && attachment.trim().length === 0) {
      return;
    }
    const currentCommentNewRoles = chatEditorOutput.selectedContacts && chatEditorOutput.selectedContacts.length > 0 ? chatEditorOutput.selectedContacts : [];
    if(this.editorType === 'Reply') {
      this.replyMessage(commentText, attachment, currentCommentNewRoles);
    } else if(this.editorType === 'EditMessage') {
      this.editMessage(commentText, attachment, currentCommentNewRoles);
    }
    this.onCancelChatEditor();
  }

  // HS-E-21: an activity entry, reply or comment written under a support access session carries the support indicator.
  isSupportMessage(message: ConversationMessage): boolean {
    return !!message?.supportAccessSessionId;
  }

  supportMemberName(message: ConversationMessage): string {
    return [message?.createdBy?.firstName, message?.createdBy?.lastName].filter(part => !!part).join(' ');
  }

  // The label names the member only when the row does not already: a reply shows "<name> replied:", and most
  // entries open with the name, but some ("On board Orbitax to ITP.") do not.
  supportRowLabel(message: ConversationMessage): string {
    const name = this.supportMemberName(message);
    const text = (message?.message ?? '').replace(/<[^>]*>/g, '');
    const nameShown = !message?.isInitialMessage || !name || text.includes(name);
    return nameShown ? 'Support activity' : `Support activity · ${name}`;
  }

  // HS-E-21-11: hovering a support row says who acted and why the row is marked. The tooltip class bolds the first line.
  supportTooltip(message: ConversationMessage): string {
    const name = this.supportMemberName(message);
    return `${name ? `${name}, ` : ''}Orbitax Support\nThis action was performed by an Orbitax Support team member.`;
  }

  viewMoreMessages() {
    let scope = this.conversation.contentScope;
    this.conversation.showLoadMoreMessage = false;
    if(this.conversation.activityData && this.conversation.activityData.activities
      && this.conversation.activityData.activities.indexOf('Orbitax')!== -1) {
      //console.log('fot orbitax activities');
      scope = GraphqlContentScope.getScope(ContentScope.OrbitaxPublication);
    }
    let query = CollaborationQuery.getConversationMessagesByConversationId(this.conversation.id, scope, this.lowestSequenceNumber - 1, false, this.impersonationId);
    this.graphQLService.executePostRequest(CollaborationConfig.CollaborationApiKey, query).pipe(take(1))
      .subscribe(payload => {
        if (payload && payload.data && payload.data.messages && payload.data.messages.length > 0) {
          //console.log('messages:', payload.data.messages);
          let messages = payload.data.messages;
          let len = messages.length;
          this.lowestSequenceNumber = Math.min(this.lowestSequenceNumber, messages[0].sequence);
          let newMessage = 0;
          for(let i = len-1;i>=0;i--) {
            if (this.conversation.messages.find(m => m.id === messages[i].id) === undefined) {
              newMessage++;
              this.conversation.messages.splice(1, 0, this.setMessagePreferences(messages[i]));
            }
          }
          if (newMessage === 0) {
            this.conversation.showLoadMoreMessage = false;
          } else {
            this.conversation.showLoadMoreMessage = true;
          }
          this.changeDetectorRef.detectChanges();
          setTimeout(() => {
            this.navigateToMessage(this.dynamicDivId+messages[len-1].id);
          }, 500);
          //navigate to dynamicDivId+message.id element
        }
      });
  }

  private prepareConversation() {
    if (this.conversation && !this.conversation.messages) {
      this.conversation.messages = [];
    }
    if (this.conversation.messages.length > 0) {
      this.lowestSequenceNumber = this.conversation.messages[0].sequence;
    } else {
      this.lowestSequenceNumber = 1000000000; //  to show for all messages
    }
    this.conversation.showLoadMoreMessage = true; // toshow load more messages all time
    if (this.conversation.initialMessage) {
      this.conversation.initialMessage.createdBy = this.conversation.activityData.author;
      this.conversation.initialMessage.isInitialMessage = true;
      this.conversation.messages.unshift(this.conversation.initialMessage);
      this.conversation.initialMessage = null;
    }
    this.conversation.messages.forEach(message => {
      this.setMessagePreferences(message);
    });
    //console.log('[lowestSequenceNumber] ', this.lowestSequenceNumber);
  }

  private getAnchorsByMessages(messages: Array<ConversationMessage>) {
    if (!messages) {
      return;
    }
    /*    messages.forEach(message => {
          var div = document.getElementById(this.dynamicDivId+message.id);
          let currentMessageAnchors = div.getElementsByTagName('a');
          if(currentMessageAnchors && currentMessageAnchors.length) {
            for (var i = 0; i < currentMessageAnchors.length; i++) {
              currentMessageAnchors[i].addEventListener('click', this.contextClickedHandler, false);
              this.anchors.push(currentMessageAnchors[i]);
            }
          }
        });*/
  }

  showMoreMessage(message: ConversationMessage) {
    message.messageDisplaylength = message.message.length;
    message.showFullMessage = true;
  }

  showMoreAttachment(message: ConversationMessage) {
    if(message.attachment && message.attachment.length) {
      message.attachmentDisplaylength = message.attachment.length;
      message.showFullAttachment = true;
    }
  }

  private setMessagePreferences(message: ConversationMessage) {
    message.showFullMessage = true;
    message.showFullAttachment = true;
    message.isMine = this.userInfo.email === message.createdBy.email;
    const moment = moment_;
    // message.createdDateWithoutTime = this.timeZoneService.getUTCDateWithoutTime(message.createdDate);
    // message.createdDateWithoutTime = moment.utc(message.createdDate).format('DD MMM YYYY');
    // message.createdDate = moment.utc(message.createdDate).format('DD MMM YYYY H:mm:ss A');
    if (message.message && message.message.length > 350) {
      message.showFullMessage = false;
      message.messageDisplaylength = 350;
    } else if (message.message) {
      message.messageDisplaylength = message.message.length;
    }
    if (message.attachment && message.attachment.length > 350) {
      message.showFullAttachment = false;
      message.attachmentDisplaylength = 350;
    } else if (message.attachment) {
      message.attachmentDisplaylength = message.attachment.length;
    }
    return message;
  }

  private downloadFile(fileId: string, isClientFile: boolean, fileName: string) {
    const downloadFileCommand = new DownloadFileCommand(fileId, '', isClientFile, fileName);
    downloadFileCommand.impersonationId = this.impersonationId;
    this.commandService.executeCommand(downloadFileCommand).pipe(take(1)).subscribe(data => {
      //console.log('Download File', data);
      if (data.Payload['DownloadUrl']) {
        this.fileManagementService.downloadOneTimeSignedUrl(data.Payload['DownloadUrl']);
      }
    });
  }

  private editMessage(message: string, attachment: string, currentCommentNewRoles: any[]) {
    this.editingMessage.isTemporary = true;
    let isOrbitaxActivity = false;
    if(this.conversation.activityData && this.conversation.activityData.activities
      && this.conversation.activityData.activities.indexOf('Orbitax') !== -1) {
      //console.log('fot orbitax activities');
      isOrbitaxActivity = true;
    }
    const command = new EditConversationMessageCommand(this.editingMessage.id, message, currentCommentNewRoles, false, isOrbitaxActivity);
    command.scope = GraphqlContentScope.getNumberScope(this.conversation.contentScope);
    command.impersonationId = this.impersonationId;
    /*this.collaborationStore.dispatch(new EditConversationMessageAction(command));*/
    this.commandService.executeCommand(command).pipe(takeUntil(this.unsubscribe$)).subscribe(editedMessage => {
      //console.log('[edited message:]', editedMessage);
      if (editedMessage && editedMessage.Payload) {
        this.collaborationStore.dispatch(new EditConversationMessageSuccessAction(JSON.parse(JSON.stringify(editedMessage))));
        const  convertedMessage = StringUtility.convertKeysPascalToCamel(editedMessage.Payload);
        this.handleEditedMessageUpdate(convertedMessage);
      }
    });
  }

  private removeMessage(message: any) {
    const messageId = message.id;
    const deleteMessageCommand = new DeleteConversationMessageCommand(messageId, true);
    deleteMessageCommand.scope = GraphqlContentScope.getNumberScope(this.conversation.contentScope);
    deleteMessageCommand.impersonationId = this.impersonationId;
    this.commandService.executeCommand(deleteMessageCommand).pipe(takeUntil(this.unsubscribe$)).subscribe(deletedMessage => {
      //console.log('[deletedMessage message:]', deletedMessage);
      if (deletedMessage && deletedMessage.Payload) {
        const  message = StringUtility.convertKeysPascalToCamel(deletedMessage.Payload);
        this.handleDeletedMessageUpdate(message.id);
      }
    });
  }

  private handleEditedMessageUpdate(editedMessage: any) {
    const index = this.conversation.messages.findIndex(m => m.id == editedMessage.id);
    if (index !== -1) {
      this.conversation.messages[index] = this.setMessagePreferences(editedMessage);
      //console.log('[edited message:]', editedMessage);
    }
  }

  private handleDeletedMessageUpdate(messageId: string) {
    if (this.conversation) {
      const index = this.conversation.messages.findIndex(m => m.id === messageId);
      if (index !== -1) {
        this.conversation.messages[index].isDeleted = true;
      }
    }
  }

  private goToPage(message) {
    const command = new PlatformCommand('NavigateToActivityLandingPageCommand', 'CollaborationApi');
    command.fieldValues['MessageId'] = message.id;
    command.fieldValues['ActiveRouteUrl'] = this.router.url;
    //command.impersonationId = this.impersonationId;
    this.fuseSplashScreenService.show();
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(response => {
      this.fuseSplashScreenService.hide();
      if (response && response.fieldValues) {
        if (response.fieldValues['IsDataTracing'] === true) {
          this.handleDataTracingNavigationResponse(response);
        } else if (response.fieldValues['RedirectUrl']) {
          this.router.navigateByUrl(response.fieldValues['RedirectUrl']);
        } else if (response.fieldValues['ErrorMessage']) {
          this.snackBarService.showError(response.fieldValues['ErrorMessage']);
        }
      }
    });
  }

  private handleDataTracingNavigationResponse(response: any) {
    const redirectUrl = response.fieldValues['RedirectUrl'];
    const worksheetId = response.fieldValues['WorksheetId'];
    const workbookId = response.fieldValues['WorkbookId'];
    const selectedRow = response.fieldValues['CellRowIndex'];
    const selectedCol = response.fieldValues['CellColumnIndex'];

    if (!worksheetId || !redirectUrl) {
      return;
    }

    this.flexSheetNavigationService.clearPaginationState(worksheetId);
    if (redirectUrl !== this.router.url) {
      this.router.navigateByUrl(redirectUrl);
    } else if (this.flexSheetService.getCurrentWorkBook()?.id === workbookId) {
      const worksheetIndex = this.flexSheetService.getWorksheets().findIndex(x => x.id === worksheetId);
      if (worksheetIndex >= 0) {
        if (this.flexSheetService.getCurrentWorkSheet()?.id  === worksheetId) {
          this.flexSheetNavigationService.navigateToCell(selectedRow || 0, selectedCol || 0);
        } else {
          this.flexSheetService.getFlexSheet().selectedSheetIndex = worksheetIndex;
          this.flexSheetService.onWorksheetChange({
            worksheet: this.flexSheetService.getWorksheets()[worksheetIndex],
            gridCol: 0,
            gridRow: 0,
            row: selectedRow || 0,
            col: selectedCol || 0
          });
        }
      }
    }
  }

  private showActivityOnDate(createdDate: Date) {
    //console.log('[created Date] ', createdDate, typeof createdDate);
    //createdDate = this.timeZoneService.getUTCDateWithoutTime(createdDate);
    const moment = moment_;
    let title = moment(createdDate).format('DD MMM YYYY');
    this.onAddNewFeed(ActivityFeedType.DateRange, '', title, false,ActivityFilterDateRangeTypes.Custom,createdDate,createdDate);
  }

  private navigateToMessage(id: string) {
    var element = document.getElementById(id);
    if(element) {
      element.scrollIntoView(false)
    }
  }

  private onAddNewFeed(type: string, id: string, title: string, isDefault: boolean, dateRangeType?: string, startDate?: Date, endDate?: Date) {
    this.newFeedInfo$.emit(new ActivityFeedInfo(
      {
        type: type,
        id: id,
        title: title,
        isDefault: isDefault,
        auditTrail: true,
        dateRangeType: dateRangeType,
        startDate: startDate,
        endDate: endDate
      }
    ));
  }

  private focusEditor(editorId: string) {
    this.changeDetectorRef.detectChanges();
    const editor = document.getElementById(this.textEditorId);
    if (editor) {
      editor.scrollIntoView(false);
      editor.focus();
    }
  }

  private replyMessage(replyText: string, attachment: string, currentCommentNewRoles: any[]) {
    const messageCommands = {};
    const replyToMessageId = this.replyToMessage ? this.replyToMessage.id : null;
    //console.log('comment:', commentText, attachment);
    //console.log('we have conversation:', this.conversation);
    let isOrbitaxActivity = false;
    if(this.conversation.activityData && this.conversation.activityData.activities
      && this.conversation.activityData.activities.indexOf('Orbitax')!== -1) {
      //console.log('fot orbitax activities');
      isOrbitaxActivity = true;
    }
    const sendMessageCommand = new SendMessageCommand(this.conversation.id, replyText, 0, null, null, null, currentCommentNewRoles, replyToMessageId, attachment, isOrbitaxActivity);
    sendMessageCommand.scope = this.conversation.contentScope;
    sendMessageCommand.messageId = Guid.create().toString();
    sendMessageCommand.impersonationId = this.impersonationId;
    sendMessageCommand.sourceConversationReferenceCode = this.feedInfo?.type.toLowerCase() === ActivityFeedType.ReferenceCode.toLowerCase() ? this.feedInfo?.id : '';
    const tempMessage = {
      'id': sendMessageCommand.messageId,
      'createdBy': {
        'email': this.userInfo.email,
        'firstName': this.userInfo.firstName,
        'lastName': this.userInfo.lastName,
        'companyId': this.userInfo.companyId
      },
      'createdDate': new Date(),
      'message': replyText,
      'isTemporary': true,
      systemMessageType: 'NON_SYSTEM_MESSAGE',
      showFullMessage: true,
      messageDisplaylength: 250,
      attachmentDisplaylength: 250
    };

    this.conversation.messages.push(tempMessage);
    this.commandService.executeCommand(sendMessageCommand).pipe(take(1)).subscribe((payload: any) => {
      //console.log('sent message:', payload);
      if (payload && payload.Payload) {
        this.collaborationStore.dispatch(new SendMessageSuccessAction(JSON.parse(JSON.stringify(payload.Payload))));
        const newMessage = StringUtility.convertKeysPascalToCamel(payload.Payload);
        newMessage.isTemporary = false;
        this.setMessagePreferences(newMessage);
        const index = this.conversation.messages.findIndex(m => m.id === tempMessage.id);
        //console.log('got index:', index);
        if (index != -1) {
          this.conversation.messages[index] = newMessage;
        } else {
          this.conversation.messages.push(newMessage);
        }
        this.conversation.messageCount += 1;
      }
    });
  }
}
